import React, { useMemo } from "react";
import {
  Button,
  Card,
  Skeleton,
  Typography,
  TextField,
  Box,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  InputLabel,
  Autocomplete,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import { styled } from "@mui/material/styles";
import { useTreasuryKRISummaryContext } from "../../../context/TreasuryKRISummaryContext";
import { useUserContext } from "../../../context/UserContext";
import { minDate, maxDate } from "../../../utils/utils";
import MonthRangeDatePickerWithPresets from "../../common/MonthRange/MonthRangeDatePickerWithPresets";

const mapBusinessEntityID = (businessEntityID) => ({
  id: businessEntityID,
  name: businessEntityID,
});

const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  padding: theme.spacing(2),
  textAlign: "center",
  color: theme.palette.text.primary,
}));

const TreasuryKRISummaryFiltersAndReports = () => {
  const { selectedUserDetailLoading, selectedUserDetail } = useUserContext();

  const {
    selectedBusinessEntityID,
    setSelectedBusinessEntityID,
    setSelectedPresetDateRange,
    selectedStartDate,
    setSelectedStartDate,
    selectedEndDate,
    setSelectedEndDate,
    hrMetricsHistoryLoading,
    hrMetricsHistoryError,
    hrMetricsHistory,
    fetchHrMetricsHistoryData,
  } = useTreasuryKRISummaryContext();

  const { firstName, lastName, regions } = selectedUserDetail || {};

  const allsfcs = useMemo(() => {
    return (
      regions
        ?.flatMap(
          (region) => region?.sfcs?.map((sfc) => sfc?.businessEntityID) || [],
        )
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b)) || []
    );
  }, [regions]);

  const mappedBusinessEntityIDs = [...(allsfcs || []).map(mapBusinessEntityID)];

  const groupByDimension = (data) =>
    data.reduce((acc, metric) => {
      const key = metric.dimension2
        ? `${metric.dimension}_${metric.dimension2}`
        : metric.dimension;

      if (!acc[key]) {
        acc[key] = [];
      }

      acc[key].push(metric);
      return acc;
    }, {});

  const getAllReportDates = (data) => {
    const dates = new Set();

    data.forEach((metric) =>
      metric.entities.forEach((entity) =>
        entity.history.forEach((h) => dates.add(h.reportDate)),
      ),
    );

    return Array.from(dates).sort();
  };

  const getHistoryValue = (metric, reportDate) => {
    const entity = metric.entities?.[0];
    if (!entity) return "N/A";

    const history = entity.history.find((h) => h.reportDate === reportDate);
    return history ? history.value : "N/A";
  };

  const groupedByDimension = groupByDimension(hrMetricsHistory);
  const reportDates = getAllReportDates(hrMetricsHistory);

  const CATEGORY_WIDTH = 150;
  const MEASURE_WIDTH = 200;
  const LIMIT_WIDTH = 200;

  const stickyCellBase = {
    position: "sticky",
    backgroundColor: "#FAFAFA",
    padding: "6px 12px",
    boxSizing: "border-box",
  };

  const handleClick = () => {
    fetchHrMetricsHistoryData({ isGlobal: false, isKRISummary: true });
  };

  return (
    <Card
      style={{
        padding: "6px",
        marginBottom: "20px",
        width: "100%",
      }}
    >
      <Grid container spacing={2} sx={{ marginBottom: 2 }}>
        <Grid item xs={12} sm={6} md={4}>
          <InputLabel id={"business-entity"}>Business Entity</InputLabel>
          <Autocomplete
            options={mappedBusinessEntityIDs}
            getOptionLabel={(option) => option?.name || ""}
            value={
              mappedBusinessEntityIDs.find(
                (item) => item.id === selectedBusinessEntityID,
              ) || null
            }
            onChange={(_, newValue) => {
              setSelectedBusinessEntityID(newValue ? newValue.id : null);
            }}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            loading={selectedUserDetailLoading}
            ListboxProps={{
              style: {
                width: "max-content",
                minWidth: "100%",
              },
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label=""
                placeholder="Select Business Entity"
                size="small"
              />
            )}
            sx={{ width: 180 }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <InputLabel id={"report-date-range"}>Report Date Range</InputLabel>
          <MonthRangeDatePickerWithPresets
            text="Report Date Range"
            minStartDate={minDate}
            maxEndDate={maxDate}
            setSelectedPresetDateRange={setSelectedPresetDateRange}
            setSelectedStartDate={setSelectedStartDate}
            setSelectedEndDate={setSelectedEndDate}
            selectedStartDate={selectedStartDate}
            selectedEndDate={selectedEndDate}
            isLoading={selectedUserDetailLoading}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          {selectedUserDetailLoading ? (
            <Box display="flex" alignItems="center" sx={{ mt: 3 }}>
              <Skeleton variant="rectangular" width={130} height={40} />
            </Box>
          ) : (
            <TextField
              id="user"
              label="User"
              value={`${firstName} ${lastName}`}
              disabled
              fullWidth
              size="small"
              sx={{ mt: 3 }}
            />
          )}
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Button
            variant="contained"
            color="primary"
            size="medium"
            onClick={handleClick}
            sx={{
              fontSize: "0.8rem",
              boxShadow: "0px 2px 2px rgba(0, 0, 0, 0.1)",
              height: "38px",
              mt: 3,
            }}
            disabled={
              selectedUserDetailLoading ||
              hrMetricsHistoryLoading ||
              !selectedBusinessEntityID ||
              !selectedStartDate ||
              !selectedEndDate
            }
          >
            Submit
          </Button>
        </Grid>
      </Grid>
      <Grid container spacing={2} sx={{ marginBottom: 2 }}>
        <Grid xs={12} md={12} lg={12}>
          <Typography>
            The below report shows the KRI Summary for the selected filters.
          </Typography>
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 12, lg: 12 }}>
          {hrMetricsHistoryLoading ? (
            <>
              <Skeleton
                variant="rectangular"
                width="100%"
                height={56}
                animation="pulse"
                style={{ marginBottom: 8 }}
              />
              {Array.from({ length: 10 }).map((_, index) => (
                <Skeleton
                  key={index}
                  variant="rectangular"
                  width="100%"
                  height={14}
                  animation="pulse"
                  style={{ marginBottom: 8 }}
                />
              ))}
            </>
          ) : !hrMetricsHistoryLoading && hrMetricsHistoryError ? (
            <Item>
              <Typography
                variant="p"
                component="p"
                sx={{
                  color: "#adadad",
                  minHeight: "250px",
                }}
              >
                There was an error in fetching the data. Please try again later.
              </Typography>
            </Item>
          ) : (!hrMetricsHistoryLoading && !hrMetricsHistory) ||
            (!hrMetricsHistoryLoading && !hrMetricsHistory?.length) ? (
            <Item>
              <Typography
                variant="p"
                component="p"
                sx={{
                  color: "#adadad",
                  minHeight: "250px",
                }}
              >
                No data available for the selected filters.
              </Typography>
            </Item>
          ) : (
            <TableContainer
              component={Paper}
              sx={{
                maxHeight: 600,
                maxWidth: "100%",
                overflow: "auto",
              }}
            >
              <Table
                stickyHeader
                sx={{
                  borderCollapse: "separate",
                  borderSpacing: 0,
                  borderRadius: "3px",
                  overflow: "auto",
                  boxShadow: "1px 1px 2px",
                  minWidth: 1200,
                }}
              >
                <TableHead sx={{ backgroundColor: "#eeeeee" }}>
                  <TableRow>
                    <TableCell
                      sx={{
                        ...stickyCellBase,
                        left: 0,
                        zIndex: 3,
                        fontWeight: "bold",
                        width: CATEGORY_WIDTH,
                        minWidth: CATEGORY_WIDTH,
                        maxWidth: CATEGORY_WIDTH,
                        whiteSpace: "nowwrap",
                      }}
                    >
                      Category
                    </TableCell>
                    <TableCell
                      sx={{
                        ...stickyCellBase,
                        left: CATEGORY_WIDTH,
                        zIndex: 3,
                        fontWeight: "bold",
                        width: MEASURE_WIDTH,
                        minWidth: MEASURE_WIDTH,
                        maxWidth: MEASURE_WIDTH,
                        whiteSpace: "nowwrap",
                      }}
                    >
                      Measure
                    </TableCell>
                    <TableCell
                      sx={{
                        ...stickyCellBase,
                        left: CATEGORY_WIDTH + MEASURE_WIDTH,
                        zIndex: 3,
                        fontWeight: "bold",
                        width: LIMIT_WIDTH,
                        minWidth: LIMIT_WIDTH,
                        maxWidth: LIMIT_WIDTH,
                        whiteSpace: "nowwrap",
                      }}
                    >
                      Limit
                    </TableCell>

                    {reportDates.map((date) => (
                      <TableCell
                        key={date}
                        sx={{
                          minWidth: 100,
                          padding: "6px 12px",
                          fontWeight: "bold",
                          whiteSpace: "nowwrap",
                        }}
                      >
                        {date.split("T")[0]}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {Object.entries(groupedByDimension).map(
                    ([dimension, metrics]) =>
                      metrics.map((metric, index) => {
                        const entity = metric.entities?.[0];

                        return (
                          <TableRow key={metric.metricId}>
                            {/* ✅ ROWSPAN APPLIED HERE */}
                            {index === 0 && (
                              <TableCell
                                rowSpan={metrics.length}
                                sx={{
                                  ...stickyCellBase,
                                  left: 0,
                                  zIndex: 2,
                                  width: CATEGORY_WIDTH,
                                  minWidth: CATEGORY_WIDTH,
                                  maxWidth: CATEGORY_WIDTH,
                                }}
                              >
                                {dimension.replace("_", " -> ")}
                              </TableCell>
                            )}

                            <TableCell
                              sx={{
                                ...stickyCellBase,
                                left: CATEGORY_WIDTH,
                                zIndex: 2,
                                width: MEASURE_WIDTH,
                                minWidth: MEASURE_WIDTH,
                                maxWidth: MEASURE_WIDTH,
                              }}
                            >
                              {metric.measure}
                            </TableCell>
                            <TableCell
                              sx={{
                                ...stickyCellBase,
                                left: CATEGORY_WIDTH + MEASURE_WIDTH,
                                zIndex: 2,
                                width: LIMIT_WIDTH,
                                minWidth: LIMIT_WIDTH,
                                maxWidth: LIMIT_WIDTH,
                              }}
                            >
                              {entity?.limit ?? "N/A"}
                            </TableCell>

                            {reportDates.map((date) => (
                              <TableCell key={date}>
                                {getHistoryValue(metric, date)}
                              </TableCell>
                            ))}
                          </TableRow>
                        );
                      }),
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Grid>
      </Grid>
    </Card>
  );
};

export default TreasuryKRISummaryFiltersAndReports;
